import { z } from 'zod';
import { US_STATE_CODES } from './enums';
import { CATALOGUE_SETTING_DEFS } from './settingsCatalogue';

/**
 * System Config (reference PC234).
 *
 * The reference has 380 settings. Only the ones this application actually reads are defined
 * here; a setting is added when the feature that reads it is built. Labels and `SCnn` codes
 * are the reference's. Settings without a code are this project's own and say so in `help`.
 */

export type SettingType = 'boolean' | 'number' | 'money' | 'text' | 'states' | 'select';
export type SettingValue = boolean | number | string | string[];

export interface SettingDef {
  key: string;
  /** Reference setting code, or null for a setting the reference was not seen to have. */
  code: string | null;
  category: string;
  label: string;
  type: SettingType;
  default: SettingValue;
  /** What the setting changes, in plain words. */
  help: string;
  /**
   * The choices of a `select` setting, as the reference lists them. For a `boolean`, the reference's
   * two words for yes and no (Y / N, YES / NO); "Yes" / "No" when absent.
   */
  options?: readonly string[];
  /**
   * A reference setting kept so System Config holds all of them, which 212Ez does not act on yet
   * (`shared/src/settingsCatalogue.ts`). The screen says so beside it.
   */
  inert?: boolean;
  /** A setting added after a deployment: shown in System Config only while this release feature is on. */
  feature?: string;
}

/**
 * "Prepaid Plan Expiry ... Attempt Day" (SC261, SC262, SC264): the day of an attempt, counted
 * from the plan's expiry. The position in this list is the number of days.
 */
export const EXPIRY_ATTEMPT_DAYS = [
  'Same Day',
  'One Day After Plan Expiry',
  'Two Day After Plan Expiry',
  'Three Day After Plan Expiry',
  'Four Day After Plan Expiry',
  'Five Day After Plan Expiry',
] as const;
/** "... Attempt failed Action" (SC261, SC263, SC265). */
export const EXPIRY_FAILED_ACTIONS = ['Suspend', 'Disconnect', 'No Action'] as const;
export type ExpiryFailedAction = (typeof EXPIRY_FAILED_ACTIONS)[number];
export const expiryAttemptDays = (label: string): number =>
  Math.max(0, (EXPIRY_ATTEMPT_DAYS as readonly string[]).indexOf(label));

export const SETTING_DEFS = [
  {
    key: 'COMPANY_NAME',
    code: 'SC87',
    category: 'General',
    label: 'Company Name',
    type: 'text',
    default: 'Demo Wireless',
    help: 'Shown on enrollment and customer screens, and as %COMPANY_NAME% in notifications.',
  },
  {
    key: 'COMPANY_LOGO_URL',
    code: null,
    category: 'General',
    label: 'Company logo address',
    type: 'text',
    default: '',
    help: "212Ez's own: the https address of your logo, used as %COMPANY_LOGO_URL% in e-mail templates, so one change here updates every template.",
    feature: 'company-logo-url',
  },
  {
    key: 'TOPUP_DISABLED',
    code: 'SC319',
    category: 'Customer Portal Setting',
    label: 'Disable Topup',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: customers cannot buy a top-up on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'RENEW_PLAN_DISABLED',
    code: 'SC320',
    category: 'Customer Portal Setting',
    label: 'Disable Renew Plan',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: customers cannot renew their plan on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'CHANGE_PLAN_DISABLED',
    code: 'SC321',
    category: 'Customer Portal Setting',
    label: 'Disable Change Plan',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: customers cannot change plan on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'AUTOPAY_DISABLED',
    code: 'SC322',
    category: 'Customer Portal Setting',
    label: 'Disable Auto Pay Plan',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: customers cannot switch Auto Pay on or off on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'UNPAID_INVOICE_ENABLED',
    code: 'SC323',
    category: 'Customer Portal Setting',
    label: 'Enable Unpaid Invoice',
    type: 'boolean',
    default: true,
    options: ['Y', 'N'],
    help: 'Y: customers see their unpaid invoices on the customer portal and can pay them.',
    feature: 'customer-portal',
  },
  {
    key: 'DISABLE_ADD_MONEY_TO_WALLET',
    code: 'SC324',
    category: 'Customer Portal Setting',
    label: 'Disable Add Money To Wallet',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: customers cannot add money to their wallet on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'ENABLED_LINE_DETAILS',
    code: 'SC325',
    category: 'Customer Portal Setting',
    label: 'Enable Line Details',
    type: 'boolean',
    default: true,
    options: ['Y', 'N'],
    help: 'Y: customers see their SIM, device and carrier on the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'SHOW_ALL_INVOICES',
    code: 'SC326',
    category: 'Customer Portal Setting',
    label: 'Show All Invoices',
    type: 'boolean',
    default: true,
    options: ['Y', 'N'],
    help: 'Y: customers see all their invoices on the customer portal, not only the unpaid ones.',
    feature: 'customer-portal',
  },
  {
    key: 'ALLOW_INACTIVE_CUSTOMER_LOGIN',
    code: 'SC327',
    category: 'Customer Portal Setting',
    label: 'Allow Inactive Customer Login',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'Y: a customer whose account is Inactive can still sign in to the customer portal.',
    feature: 'customer-portal',
  },
  {
    key: 'PREPAID_ENROLLMENT_PREFIX',
    code: 'SC31',
    category: 'Enrollment',
    label: 'Prepaid Enrollment Prefix',
    type: 'text',
    default: 'LC',
    help: 'Letters placed before the running number of every new enrollment ID.',
  },
  {
    key: 'EMAIL_MANDATORY',
    code: 'SC10',
    category: 'Enrollment',
    label: 'Email Mandatory (Prepaid Postpaid Enrollment)',
    type: 'boolean',
    default: true,
    help: 'When on, enrollment step 2 cannot continue without an email address.',
  },
  {
    key: 'PP_ENROLLMENT_DISABLED_STATES',
    code: 'SC09',
    category: 'Enrollment',
    label: 'Prepaid Enrollment Disabled state',
    type: 'states',
    default: [],
    help: 'States in which a prepaid or postpaid enrollment cannot be started.',
  },
  {
    key: 'CASH_PAYMENT',
    code: 'SC26',
    category: 'Enrollment',
    label: 'Cash Payment',
    type: 'boolean',
    default: true,
    help: 'Offer cash as a payment method at the end of enrollment.',
  },
  {
    key: 'PREPAID_SKIP_PAYMENT',
    code: 'SC27',
    category: 'Enrollment',
    label: 'Prepaid Skip Payment',
    type: 'boolean',
    default: false,
    help: 'Allow users with the skip-payment permission to complete an enrollment without payment.',
  },
  {
    key: 'SKIP_ACTIVATION_FEE',
    code: 'SC33',
    category: 'Enrollment',
    label: 'Skip Activation Fee',
    type: 'boolean',
    default: false,
    help: 'When on, the plan’s activation fee is not charged at enrollment.',
  },
  {
    key: 'SERVICE_ADDRESS_DUPLICATE_CHECK',
    code: 'SC45',
    category: 'Enrollment',
    label: 'Service Address Duplicate Check',
    type: 'boolean',
    default: false,
    help: 'Refuse an enrollment whose service address already belongs to a customer who is not inactive.',
  },
  {
    key: 'REGULATORY_FEE_CENTS',
    code: null,
    category: 'Enrollment',
    label: 'Regulatory fee per line',
    type: 'money',
    default: 0,
    help: 'Added to every enrollment as "Total Regulatory Fee". The reference charges such a fee but where it is configured was not observed, so this setting is this project’s own.',
  },
  {
    key: 'FRAUD_EMAIL_ENABLED',
    code: 'SC357',
    category: 'Fraud Prevention',
    label: 'Enable Fraud Prevention for Email',
    type: 'boolean',
    default: false,
    help: 'Limit how many enrollments may use the same email address.',
  },
  {
    key: 'FRAUD_EMAIL_MAX_ORDERS',
    code: 'SC360',
    category: 'Fraud Prevention',
    label: 'Number of Orders Allowed in Email Fraud Prevention',
    type: 'number',
    default: 3,
    help: 'Enrollments allowed per email address within the period below.',
  },
  {
    key: 'FRAUD_EMAIL_DAYS',
    code: 'SC362',
    category: 'Fraud Prevention',
    label: 'Number of Days For Email Fraud Prevention',
    type: 'number',
    default: 30,
    help: 'Length of the period, in days.',
  },
  {
    key: 'FRAUD_REVIEW_QUEUE',
    code: null,
    category: 'Fraud Prevention',
    label: 'Send orders over the fraud limits to the review queue',
    type: 'boolean',
    default: false,
    help: 'Off: an order over the email limit is refused. On: it is accepted as "Pending Audit" and cannot be completed until someone approves it in Review > Fraud Orders Review Queue. The reference has such a queue; which rule fills it was not observed, so this setting is this project’s own.',
  },
  {
    key: 'TAX_ENABLED',
    code: null,
    category: 'Tax',
    label: 'Calculate tax',
    type: 'boolean',
    default: false,
    help: "Off: tax on every charge is 0. On: tax is the rate of the customer's state in Settings > Tax Config > Tax Rates. The reference sends each charge to a tax service (SureTax) instead; the rate table is this project's stand-in for it.",
  },
  {
    key: 'TAX_ON_NEW_SIGNUP',
    code: null,
    category: 'Tax',
    label: 'Tax a new enrollment',
    type: 'boolean',
    default: true,
    help: 'With tax on, tax the total of an enrollment (plan, advance months, activation fee, handset, regulatory fee, less discount). The reference has a switch of this kind per transaction type; its exact wording was not recorded.',
  },
  {
    key: 'TAX_ON_TOPUP',
    code: null,
    category: 'Tax',
    label: 'Tax a top-up',
    type: 'boolean',
    default: true,
    help: 'With tax on, tax the price of a top-up.',
  },
  {
    key: 'TAX_ON_PLAN_RENEW',
    code: null,
    category: 'Tax',
    label: 'Tax a plan renewal',
    type: 'boolean',
    default: true,
    help: 'With tax on, tax the price of a plan renewal after its discounts.',
  },
  {
    key: 'TAX_ON_EQUIPMENT',
    code: 'SC687',
    category: 'Inventory',
    label: 'Handle Tax Amount on Equipment Purchase??',
    type: 'boolean',
    default: true,
    help: 'With tax on, tax equipment sold to an existing customer.',
  },
  {
    key: 'RENEW_CUSTOMER_WALLET',
    code: 'SC92',
    category: 'General',
    label: 'Renew Customer Wallet',
    type: 'boolean',
    default: false,
    help: "On: each plan expiry attempt first tries to renew the plan from the customer's wallet, Auto Pay or not.",
  },
  {
    key: 'RENEW_AUTO_PAY',
    code: 'SC93',
    category: 'General',
    label: 'Renew Auto Pay',
    type: 'boolean',
    default: true,
    help: 'On: each plan expiry attempt renews the plan of a customer with Auto Pay. Auto Pay here pays from the wallet only: no payment gateway is connected.',
  },
  {
    key: 'SKIP_PLAN_PREPAID_STATUS_ON_EXPIRY',
    code: 'SC94',
    category: 'General',
    label: 'Skip Plan Prepaid Account Status On Expiry',
    type: 'boolean',
    default: false,
    help: 'On: the attempts still try to renew, but a failed attempt never changes the account status.',
  },
  {
    key: 'PLAN_EXPIRY_ENABLED',
    code: null,
    category: 'Plan Change/Renew Config',
    label: 'Act on expired plans',
    type: 'boolean',
    default: false,
    help: "This project's master switch, off by default. Off: nothing happens when a plan runs out. On: the three attempts below run on their days (SC261 to SC265). The reference's settings are used as recorded; how the reference runs them was not observed, so the order (renew, then the action of a failed attempt) is this project's reading. The plan's grace period fields are stored but not acted on.",
  },
  {
    key: 'PLAN_EXPIRY_FIRST_ATTEMPT_DAY',
    code: 'SC261',
    category: 'Plan Change/Renew Config',
    label: 'Prepaid Plan Expiry First Attempt Day',
    type: 'select',
    options: EXPIRY_ATTEMPT_DAYS,
    default: 'Same Day',
    help: 'The day of the first attempt, counted from the expiry.',
  },
  {
    key: 'PLAN_EXPIRY_FIRST_ATTEMPT_ACTION',
    code: 'SC261',
    category: 'Plan Change/Renew Config',
    label: 'First Attempt failed Action',
    type: 'select',
    options: EXPIRY_FAILED_ACTIONS,
    default: 'Suspend',
    help: 'What happens to the account when the first attempt cannot renew the plan.',
  },
  {
    key: 'PLAN_EXPIRY_SECOND_ATTEMPT_DAY',
    code: 'SC262',
    category: 'Plan Change/Renew Config',
    label: 'Prepaid Plan Expiry SECOND Attempt Day',
    type: 'select',
    options: EXPIRY_ATTEMPT_DAYS.slice(1),
    default: 'Two Day After Plan Expiry',
    help: 'The day of the second attempt. It runs after the first, whatever the days say.',
  },
  {
    key: 'PLAN_EXPIRY_SECOND_ATTEMPT_ACTION',
    code: 'SC263',
    category: 'Plan Change/Renew Config',
    label: 'Second Attempt failed Action',
    type: 'select',
    options: EXPIRY_FAILED_ACTIONS,
    default: 'No Action',
    help: 'What happens to the account when the second attempt fails.',
  },
  {
    key: 'PLAN_EXPIRY_THIRD_ATTEMPT_DAY',
    code: 'SC264',
    category: 'Plan Change/Renew Config',
    label: 'Prepaid Plan Expiry THIRD Attempt Day',
    type: 'select',
    options: EXPIRY_ATTEMPT_DAYS.slice(2),
    default: 'Five Day After Plan Expiry',
    help: 'The day of the last attempt.',
  },
  {
    key: 'PLAN_EXPIRY_THIRD_ATTEMPT_ACTION',
    code: 'SC265',
    category: 'Plan Change/Renew Config',
    label: 'Third Attempt failed Action',
    type: 'select',
    options: EXPIRY_FAILED_ACTIONS,
    default: 'Disconnect',
    help: 'What happens to the account when the last attempt fails. No further attempt follows.',
  },
  {
    key: 'RESET_AUTOPAY_RETRY_ON_WALLET',
    code: 'SC277',
    category: 'Plan Change/Renew Config',
    label: 'Reset autopay retry counter when amount updated in wallet',
    type: 'boolean',
    default: false,
    help: 'On: money added to the wallet of a customer whose plan has run out (and who is not disconnected) starts the attempts again from the first, which then runs at once.',
  },
  {
    key: 'ALLOW_INSTANT_CUSTOM_PLANPURCHASE',
    code: 'SC251',
    category: 'Plan Change/Renew Config',
    label: 'Allow Instant/Custom Plan Purchase For Active Customer (Plan Renew)',
    type: 'boolean',
    default: true,
    help: 'Off: a plan that has not run out can be renewed only On Expiry. A plan that has run out is always renewed Instant.',
  },
  {
    key: 'KEEP_EXPIRY_DEFAULT_VALUE',
    code: 'SC252',
    category: 'Plan Change/Renew Config',
    label: 'Keep Expiry Default Value (Plan Renew)',
    type: 'boolean',
    default: true,
    help: 'Whether "Keep the Expiry as Original Date" starts ticked on Plan Renew.',
  },
  {
    key: 'ALLOW_INSTANT_CUSTOM_PLAN_CHANGE',
    code: 'SC253',
    category: 'Plan Change/Renew Config',
    label: 'Allow Instant/Custom Plan Purchase For Active Customer(Plan Change)',
    type: 'boolean',
    default: true,
    help: 'Off: a plan that has not run out can be changed only On Expiry.',
  },
  {
    key: 'KEEP_EXPIRY_DEFAULT_VALUE_PLAN_CHANGE',
    code: 'SC254',
    category: 'Plan Change/Renew Config',
    label: 'Keep Expiry Default Value(Plan Change)',
    type: 'boolean',
    default: false,
    help: 'Whether "Keep the Expiry as Original Date" starts ticked on Plan Change.',
  },
  {
    key: 'ALLOW_LATE_FEE_AFTER_PLAN_EXPIRY',
    code: 'SC269',
    category: 'Plan Change/Renew Config',
    label: 'Allow Late Fee After Plan Expiry',
    type: 'boolean',
    default: false,
    help: 'On: a renewal or plan change of a plan that has run out carries the late fee below, which the agent may skip ("Skip late Fee").',
  },
  {
    key: 'LATE_FEE_AFTER_PLAN_EXPIRY',
    code: 'SC270',
    category: 'Plan Change/Renew Config',
    label: 'Late Fee After Plan Expiry(Applicable Per Account No)',
    type: 'money',
    default: 0,
    help: 'The late fee, once per account.',
  },
  {
    key: 'ALLOW_RESTORE_FEE_FOR_SUSPENDED_ACCOUNT',
    code: 'SC271',
    category: 'Plan Change/Renew Config',
    label: 'Allow Restore Fee For Suspend Account',
    type: 'boolean',
    default: false,
    help: 'On: bringing a suspended line back (by renewal, plan change or Restore) carries the restore fee below, which the agent may skip ("Skip Restore Fee").',
  },
  {
    key: 'RESTORE_FEE_FOR_SUSPENDED_ACCOUNT',
    code: 'SC272',
    category: 'Plan Change/Renew Config',
    label: 'Restore Fee For Suspend Account(Applicable Per Line and upto 3 lines)',
    type: 'money',
    default: 0,
    help: 'The restore fee, per line. A customer here is one line.',
  },
  {
    key: 'COMMISSION_MATRIX_ENABLED',
    code: 'SC308',
    category: 'Generate Commission',
    label: 'Commission matrix',
    type: 'boolean',
    default: true,
    help: 'Off: no matrix commission is earned.',
  },
  {
    key: 'MATRIX_COMMISSION_TYPE',
    code: 'SC593',
    category: 'Generate Commission',
    label: 'Matrix Commission Type',
    type: 'select',
    options: ['FLAT', 'PERCENTAGE', 'FLAT & PERCENTAGE'],
    default: 'FLAT & PERCENTAGE',
    help: 'Which amounts a matrix line takes: a fixed amount ($), a percent of the plan price (%), or both, when the greater is paid.',
  },
  {
    key: 'ALLOW_RECURRING_MATRIX_COMMISSION',
    code: 'SC903',
    category: 'Generate Commission',
    label: 'Allow Recurring commission in Matrix commission',
    type: 'boolean',
    default: true,
    help: 'Off: a matrix can only be Onetime.',
  },
  {
    key: 'LOGIN_PASSWORD_EXPIRY_DAYS',
    code: 'SC99',
    category: 'User Setting',
    label: 'Login Password Expiry Days',
    type: 'number',
    default: 0,
    help: 'Days a password lasts. After that the user chooses a new one at sign-in. 0: passwords do not expire.',
    feature: 'password-expiry',
  },
  {
    key: 'AGENT_PASSWORD_EXPIRY_NO_LOGIN_DAYS',
    code: 'SC1490',
    category: 'User Setting',
    label: 'Numbers of days to expiry agent password if not logged in',
    type: 'number',
    default: 0,
    help: 'An agent (any user but an administrator) who has not signed in for this many days chooses a new password at sign-in. 0: never.',
    feature: 'login-limits',
  },
  {
    key: 'PREVENT_MULTIPLE_LOGIN',
    code: 'SC227',
    category: 'User Setting',
    label: 'Prevent User Multiple Login',
    type: 'boolean',
    default: true,
    help: 'On: a user may be signed in on at most "Allowed Multiple Login" devices at once. Off: no limit.',
    feature: 'login-limits',
  },
  {
    key: 'ALLOWED_MULTIPLE_LOGIN',
    code: 'SC228',
    category: 'User Setting',
    label: 'Allowed Multiple Login',
    type: 'select',
    options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    default: '1',
    help: 'Sessions a user may have at once while Prevent User Multiple Login is on.',
    feature: 'login-limits',
  },
  // Acting since 2026-10-23; shown with the full catalogue (switch system-config-all). Defaults keep the
  // behaviour 212Ez had before.
  {
    key: 'NOTES_CALLER_ID',
    code: 'SC103',
    category: 'General',
    label: 'Customer Notes Caller ID',
    type: 'select',
    options: ['Required', 'Optional'],
    default: 'Optional',
    help: 'Required: a note on a customer cannot be added without the Caller ID.',
    feature: 'system-config-all',
  },
  {
    key: 'CARRIER_CHANGE_DEACTIVATE_OLD_MDN',
    code: 'SC140',
    category: 'Inventory',
    label: 'Carrier Change Old MDN Deactivate?',
    type: 'boolean',
    options: ['Y', 'N'],
    default: true,
    help: 'Y: Change Carrier asks the old carrier to disconnect the old telephone number. N: the old number is left as it is there.',
    feature: 'system-config-all',
  },
  {
    key: 'DISCONNECT_WITHOUT_MDN',
    code: 'SC397',
    category: 'CSR Config',
    label: 'Disconnect Customer Without MDN',
    type: 'boolean',
    options: ['Y', 'N'],
    default: false,
    help: 'Y: an account without a telephone number can be disconnected Electronically too (the carrier is not asked: there is no line there).',
    feature: 'system-config-all',
  },
  {
    key: 'VALIDATE_ZIP_FROM_CARRIER',
    code: 'SC72',
    category: 'Enrollment',
    label: 'Validation zip code from carrier',
    type: 'boolean',
    default: false,
    options: ['Y', 'N'],
    help: 'ZIP carrier routing: Y asks each carrier that covers the ZIP whether it serves it, through its connection, when an enrollment starts; a carrier that says no is not offered. A carrier whose connector has no coverage check is kept.',
    feature: 'system-config-all',
  },
  {
    key: 'PRIORITY_WISE_CARRIER_LIST_FOR_CSA_IN_ENROLLMENT',
    code: 'SC73',
    category: 'Enrollment',
    label: 'Priority Wise Carrier List For CSA In Enrollment',
    type: 'text',
    default: '',
    help: 'ZIP carrier routing: carrier codes in order of preference, comma separated (TMB,ATT,VZW). The first one that covers the ZIP is picked; carriers not listed come after, by code.',
    feature: 'system-config-all',
  },
  {
    key: 'PROHIBITED_EMAIL_DOMAINS',
    code: 'SC1648',
    category: 'Notification Config',
    label: 'Prohibited Email Domains (Comma Separated Ex: abc.com, test@xyz.com)',
    type: 'text',
    default: '',
    help: 'E-mail domains (abc.com) or addresses (test@xyz.com) a customer may not give, at enrollment or in Customer Information.',
    feature: 'system-config-all',
  },
  {
    key: 'DAILY_ENROLLMENT_LIMIT_ON',
    code: 'SC82',
    category: 'Agent Compliance',
    label: 'Allow Daily Enrollment Limit',
    type: 'boolean',
    options: ['YES', 'NO'],
    default: false,
    help: 'YES: an agent (any user but an administrator) can start at most "Daily Enrollment Max Limit" enrollments a day (UTC).',
    feature: 'system-config-all',
  },
  {
    key: 'DAILY_ENROLLMENT_MAX',
    code: 'SC83',
    category: 'Agent Compliance',
    label: 'Daily Enrollment Max Limit',
    type: 'select',
    options: [
      '1',
      '2',
      '3',
      '50',
      '100',
      '150',
      '200',
      '250',
      '300',
      '350',
      '400',
      '450',
      '500',
      '550',
      '600',
      '650',
      '700',
      '750',
      '800',
      '850',
      '900',
      '950',
      '1000',
      '1050',
      '1100',
      '1150',
      '1200',
      '1250',
      '1300',
      '1350',
      '1400',
      '1450',
      '1500',
    ],
    default: '50',
    help: 'Enrollments an agent can start in a day while the daily limit is on.',
    feature: 'system-config-all',
  },
  {
    key: 'MONTHLY_ENROLLMENT_LIMIT_ON',
    code: 'SC84',
    category: 'Agent Compliance',
    label: 'Allow Monthly Enrollment Limit',
    type: 'boolean',
    options: ['YES', 'NO'],
    default: false,
    help: 'YES: an agent can start at most "Monthly Enrollment Max Limit" enrollments in a calendar month (UTC).',
    feature: 'system-config-all',
  },
  {
    key: 'MONTHLY_ENROLLMENT_MAX',
    code: 'SC85',
    category: 'Agent Compliance',
    label: 'Monthly Enrollment Max Limit',
    type: 'select',
    options: [
      '1',
      '2',
      '3',
      '4',
      '5',
      '1500',
      '2000',
      '2500',
      '3000',
      '3500',
      '4000',
      '4500',
      '5000',
      '5500',
      '6000',
      '6500',
      '7000',
      '7500',
      '8000',
      '8500',
      '9000',
      '9500',
      '10000',
    ],
    default: '1500',
    help: 'Enrollments an agent can start in a month while the monthly limit is on.',
    feature: 'system-config-all',
  },
  {
    key: 'DATA_TOPUP_LIMIT_ON',
    code: 'SC370',
    category: 'Fraud Prevention',
    label: 'Enable Fraud Prevention for DATA Topup',
    type: 'boolean',
    options: ['Y', 'N'],
    default: false,
    help: 'Y: a line can get at most "Number of DATA Topup Allowed in Billing Period" data top-ups in its billing period (the last 30 days).',
    feature: 'system-config-all',
  },
  {
    key: 'DATA_TOPUP_MAX_PER_PERIOD',
    code: 'SC371',
    category: 'Fraud Prevention',
    label: 'Number of DATA Topup Allowed in Billing Period',
    type: 'number',
    default: 3,
    help: 'Data top-ups a line can get in its billing period while the limit is on.',
    feature: 'system-config-all',
  },
  {
    key: 'MIDCYCLE_PLAN_CHANGE_LIMIT_ON',
    code: 'SC259',
    category: 'Plan Change/Renew Config',
    label: 'Allow Midcycle Plan Change Restriction',
    type: 'boolean',
    options: ['Y', 'N'],
    default: false,
    help: 'Y: a line can change plan Instantly at most "Midcycle Plan Change Limit Allowed Count" times in its plan cycle (the last 30 days).',
    feature: 'system-config-all',
  },
  {
    key: 'MIDCYCLE_PLAN_CHANGE_MAX',
    code: 'SC260',
    category: 'Plan Change/Renew Config',
    label: 'Midcycle Plan Change Limit Allowed Count',
    type: 'select',
    options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
    default: '1',
    help: 'Instant plan changes a line can have in its plan cycle while the restriction is on.',
    feature: 'system-config-all',
  },
] as const satisfies readonly SettingDef[];

/**
 * Every setting of System Config: those 212Ez acts on, then the other reference settings, kept
 * and shown as not used yet.
 */
export const ALL_SETTING_DEFS = [...SETTING_DEFS, ...CATALOGUE_SETTING_DEFS] as const;

export type SettingKey = (typeof ALL_SETTING_DEFS)[number]['key'];

/** Value type of each setting, by its key. */
export type SettingValues = {
  [D in (typeof ALL_SETTING_DEFS)[number] as D['key']]: D['type'] extends 'boolean'
    ? boolean
    : D['type'] extends 'number' | 'money'
      ? number
      : D['type'] extends 'states'
        ? string[]
        : string;
};

const BY_KEY = new Map<string, SettingDef>(ALL_SETTING_DEFS.map((d) => [d.key, d]));

/**
 * Whether an e-mail address is refused by "Prohibited Email Domains" (SC1648): the list names domains
 * (abc.com, matching the address's domain) or whole addresses (test@xyz.com), comma separated.
 */
export function emailProhibited(email: string, list: string): boolean {
  const address = email.trim().toLowerCase();
  if (!address) return false;
  const domain = address.slice(address.lastIndexOf('@') + 1);
  return list
    .split(',')
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
    .some((x) => (x.includes('@') ? x === address : x.replace(/^@/, '') === domain));
}
export const getSettingDef = (key: string): SettingDef | undefined => BY_KEY.get(key);

/** Zod schema for one setting's value, by type. */
export function settingValueSchema(def: SettingDef): z.ZodTypeAny {
  switch (def.type) {
    case 'boolean':
      return z.boolean();
    case 'number':
      return z.number().int().min(0).max(100_000);
    case 'money':
      return z.number().int().min(0).max(10_000_00);
    case 'states':
      return z.array(z.enum(US_STATE_CODES)).max(60);
    case 'select':
      return z.enum((def.options ?? ['']) as [string, ...string[]], {
        errorMap: () => ({ message: 'Select one of the choices' }),
      });
    default:
      return z.string().trim().max(200);
  }
}

/** Body of `PATCH /system-configs`: any subset of settings. */
export const settingsUpdateSchema = z.object({
  values: z.record(z.unknown()).refine((v) => Object.keys(v).length > 0, 'Nothing to save'),
});

export interface SettingDto extends Omit<SettingDef, 'default'> {
  default: SettingValue;
  value: SettingValue;
  updatedAt: string | null;
  updatedBy: { id: string; name: string; loginId: string } | null;
}

/** One row of "Config Changes logs". */
export interface SettingChangeDto {
  id: string;
  key: string;
  code: string | null;
  label: string;
  from: SettingValue;
  to: SettingValue;
  by: { id: string; name: string; loginId: string } | null;
  at: string;
}

/**
 * "Used system configurations" (release switch `used-system-configurations`): a setting a page
 * reads, with its value as System Config shows it.
 */
export interface UsedSettingDto {
  key: SettingKey;
  code: string;
  label: string;
  value: string;
}

/** The value of a setting as words: the reference's yes/no words, a list, or the value itself. */
export function settingValueText(def: SettingDef, value: SettingValue): string {
  if (def.type === 'boolean') {
    const [yes, no] = def.options ?? ['Yes', 'No'];
    return value ? (yes ?? 'Yes') : (no ?? 'No');
  }
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  return value === '' ? 'Not set' : String(value);
}
